import { cn } from '@/lib/utils';
import React from 'react';
import { IconProps } from './icon';

interface GradientIconProps extends IconProps {
  variant?: string;
  startColor?: string;
  endColor?: string;
}

// Color combinations for each letter of the alphabet
const letterColors: Record<string, { startColor: string; endColor: string }> = {
  'A': { startColor: '#FF6B6B', endColor: '#4ECDC4' },
  'B': { startColor: '#A8E6CF', endColor: '#FF8B94' },
  'C': { startColor: '#FFD93D', endColor: '#6BCF7F' },
  'D': { startColor: '#FF9A8B', endColor: '#4FC3F7' },
  'E': { startColor: '#B39DDB', endColor: '#FFB74D' },
  'F': { startColor: '#81C784', endColor: '#FF8A65' },
  'G': { startColor: '#FFB74D', endColor: '#64B5F6' },
  'H': { startColor: '#F06292', endColor: '#4DD0E1' },
  'I': { startColor: '#4FC3F7', endColor: '#FFD54F' },
  'J': { startColor: '#FF8A65', endColor: '#81C784' },
  'K': { startColor: '#9575CD', endColor: '#FFB74D' },
  'L': { startColor: '#4DB6AC', endColor: '#FF8A65' },
  'M': { startColor: '#FFB74D', endColor: '#7986CB' },
  'N': { startColor: '#81C784', endColor: '#FF8A65' },
  'O': { startColor: '#FF8A65', endColor: '#4FC3F7' },
  'P': { startColor: '#64B5F6', endColor: '#FFB74D' },
  'Q': { startColor: '#FFB74D', endColor: '#81C784' },
  'R': { startColor: '#F06292', endColor: '#4DD0E1' },
  'S': { startColor: '#4FC3F7', endColor: '#FFD54F' },
  'T': { startColor: '#FF8A65', endColor: '#81C784' },
  'U': { startColor: '#9575CD', endColor: '#FFB74D' },
  'V': { startColor: '#4DB6AC', endColor: '#FF8A65' },
  'W': { startColor: '#FFB74D', endColor: '#7986CB' },
  'X': { startColor: '#81C784', endColor: '#FF8A65' },
  'Y': { startColor: '#FF8A65', endColor: '#4FC3F7' },
  'Z': { startColor: '#64B5F6', endColor: '#FFB74D' },
};

// Color combinations for numbers 0-9
const numberColors: Record<string, { startColor: string; endColor: string }> = {
  '0': { startColor: '#FF6B6B', endColor: '#4ECDC4' },
  '1': { startColor: '#A8E6CF', endColor: '#FF8B94' },
  '2': { startColor: '#FFD93D', endColor: '#6BCF7F' },
  '3': { startColor: '#FF9A8B', endColor: '#4FC3F7' },
  '4': { startColor: '#B39DDB', endColor: '#FFB74D' },
  '5': { startColor: '#81C784', endColor: '#FF8A65' },
  '6': { startColor: '#FFB74D', endColor: '#64B5F6' },
  '7': { startColor: '#F06292', endColor: '#4DD0E1' },
  '8': { startColor: '#4FC3F7', endColor: '#FFD54F' },
  '9': { startColor: '#FF8A65', endColor: '#81C784' },
};

// Special variants
const specialVariants: Record<string, { startColor: string; endColor: string }> = {
  'dynamic_credit_line': { startColor: '#D1F11F', endColor: '#C4B9FE' },
  'invoice_financing': { startColor: '#0C1C1C', endColor: '#C4B9FE' },
};

export const GradientIcon: React.FC<GradientIconProps> = ({ 
  className, 
  variant, 
  startColor, 
  endColor 
}) => {
  let colors = { startColor: '#FF6B6B', endColor: '#4ECDC4' }; // default colors

  if (variant) {
    if (letterColors[variant.toUpperCase()]) {
      colors = letterColors[variant.toUpperCase()];
    } else if (numberColors[variant]) {
      colors = numberColors[variant];
    } else if (specialVariants[variant]) {
      colors = specialVariants[variant];
    }
  } else if (startColor && endColor) {
    colors = { startColor, endColor };
  }

  return (
    <div
      className={cn("size-4 rounded-full shrink-0", className)}
      style={{ background: `conic-gradient(from 180deg at 50% 50%, ${colors.startColor} 0deg, ${colors.endColor} 360deg)` }}
    />
  );
};
